// Apple Music library integration (MusicKit).
//
// Two tokens are involved: the developer token (signed by our server, see
// server/appleMusic.js) and the Music User Token ("MUT") proving the user
// authorized us. How the MUT is obtained is platform-specific:
//
//  - Web: MusicKit JS v3 handles the popup authorization flow and persists
//    the MUT in its own localStorage — we read it off the instance.
//  - Capacitor iOS: MusicKit JS's popup handshake breaks in WKWebView
//    (window.open has no opener relationship), so a native plugin
//    (AppleMusicAuth) requests authorization via the MusicKit framework and
//    returns a MUT that we persist in webview localStorage ourselves.
//
// Everything after authorization is plain fetch against api.music.apple.com
// with both tokens attached — identical on both platforms.

import { registerPlugin } from '@capacitor/core';
import { IS_CAPACITOR } from './config';
import {
  getAppleMusicDeveloperToken,
  getStoredAppleMusicUserToken,
  saveAppleMusicUserToken,
  deleteAppleMusicUserToken,
} from './authClient';
import { getTrackIsrc } from './deezerClient';
import { artistsMatch, titleVariants, pickBestSongMatch } from '../utils/appleMusicMatch';

const API_ROOT = 'https://api.music.apple.com';
const MUSICKIT_SRC = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
const IOS_MUT_STORAGE_KEY = 'stellar_apple_music_user_token';

const AppleMusicAuth = IS_CAPACITOR ? registerPlugin('AppleMusicAuth') : null;

// --- Developer token (cached; the server re-signs daily with 7d validity) ---

let devToken = null;
let devTokenPromise = null;

async function fetchDeveloperToken(force = false) {
  if (force) {
    devToken = null;
    devTokenPromise = null;
  }
  if (devToken) return devToken;
  if (!devTokenPromise) {
    devTokenPromise = getAppleMusicDeveloperToken()
      .then(async (res) => {
        if (!res.ok) throw new Error('Apple Music is not available right now.');
        const data = await res.json();
        if (!data.token) throw new Error('Apple Music is not available right now.');
        devToken = data.token;
        return devToken;
      })
      .catch((err) => {
        devTokenPromise = null; // transient — allow retry
        throw err;
      });
  }
  return devTokenPromise;
}

// --- Web: MusicKit JS loading + configuration (singletons) ---

let scriptPromise = null;

function loadMusicKitScript() {
  if (window.MusicKit) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const onLoaded = () => resolve();
      document.addEventListener('musickitloaded', onLoaded, { once: true });
      const script = document.createElement('script');
      script.src = MUSICKIT_SRC;
      script.async = true;
      script.onerror = () => {
        document.removeEventListener('musickitloaded', onLoaded);
        script.remove();
        scriptPromise = null; // a network blip must not poison the singleton
        reject(new Error('Could not load Apple Music. Check your connection and try again.'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

let instancePromise = null;

// Pre-warm this before the user clicks Connect: authorize() must run inside
// the click gesture to survive popup blockers, so the script load and token
// fetch need to have already happened.
export function getMusicKitInstance() {
  if (!instancePromise) {
    instancePromise = (async () => {
      const [token] = await Promise.all([fetchDeveloperToken(), loadMusicKitScript()]);
      await window.MusicKit.configure({
        developerToken: token,
        app: { name: 'Stellar', build: '1.0' },
      });
      return window.MusicKit.getInstance();
    })().catch((err) => {
      instancePromise = null; // retriable
      throw err;
    });
  }
  return instancePromise;
}

// --- Authorization state (unified across platforms) ---
//
// The Music User Token has two homes: the device that minted it (MusicKit's
// own localStorage on web, our key on iOS) and the user's account on our
// server. The account copy is what makes "connect once, connected
// everywhere" work — a device with no local token falls back to it.

let accountTokenPromise = null;

function rememberAccountToken(token) {
  accountTokenPromise = Promise.resolve(token || null);
}

function fetchAccountToken() {
  if (!accountTokenPromise) {
    accountTokenPromise = getStoredAppleMusicUserToken()
      .then(async (res) => {
        if (!res.ok) throw new Error('user token fetch failed');
        const data = await res.json();
        return data.token || null;
      })
      .catch(() => {
        accountTokenPromise = null; // transient — retry on next check
        return null;
      });
  }
  return accountTokenPromise;
}

// Best-effort: cross-device connection is a convenience, so a failed sync
// must never fail the connect (or disconnect) that just succeeded locally.
async function syncAccountToken(token) {
  rememberAccountToken(token);
  try {
    if (token) {
      await saveAppleMusicUserToken(token);
    } else {
      await deleteAppleMusicUserToken();
    }
  } catch {
    accountTokenPromise = null; // unknown server state — refetch next time
  }
}

async function getLocalMusicUserToken() {
  if (IS_CAPACITOR) return localStorage.getItem(IOS_MUT_STORAGE_KEY) || null;
  try {
    const instance = await getMusicKitInstance();
    return instance.isAuthorized ? instance.musicUserToken : null;
  } catch {
    // MusicKit failed to load — the account token can still carry an export.
    return null;
  }
}

async function getMusicUserToken() {
  return (await getLocalMusicUserToken()) || (await fetchAccountToken());
}

export async function isAppleMusicAuthorized() {
  return !!(await getMusicUserToken());
}

export async function authorizeAppleMusic() {
  if (IS_CAPACITOR) {
    const token = await fetchDeveloperToken();
    const result = await AppleMusicAuth.authorize({ developerToken: token });
    if (!result?.musicUserToken) {
      throw new Error('Apple Music authorization failed.');
    }
    localStorage.setItem(IOS_MUT_STORAGE_KEY, result.musicUserToken);
    await syncAccountToken(result.musicUserToken);
    return;
  }
  const instance = await getMusicKitInstance();
  await instance.authorize();
  if (!instance.isAuthorized) {
    throw new Error('Apple Music authorization was cancelled.');
  }
  await syncAccountToken(instance.musicUserToken);
}

export async function unauthorizeAppleMusic() {
  // Disconnect everywhere: local credential and the account copy, so other
  // devices don't silently stay connected.
  if (IS_CAPACITOR) {
    // The OS-level permission can't be revoked programmatically; dropping the
    // stored user token is what "disconnect" means here.
    localStorage.removeItem(IOS_MUT_STORAGE_KEY);
  } else {
    try {
      const instance = await getMusicKitInstance();
      await instance.unauthorize();
    } catch {
      // Nothing local to tear down if MusicKit never loaded.
    }
  }
  await syncAccountToken(null);
}

// Web only: MusicKit emits authorizationStatusDidChange when the user
// completes or revokes authorization. Returns an unsubscribe function; on
// iOS there are no events, so this is a no-op (the hook updates state after
// its own connect/disconnect calls).
export async function onAuthorizationChange(callback) {
  if (IS_CAPACITOR) return () => {};
  const instance = await getMusicKitInstance();
  const handler = () => callback(instance.isAuthorized);
  instance.addEventListener('authorizationStatusDidChange', handler);
  return () => instance.removeEventListener('authorizationStatusDidChange', handler);
}

// --- Apple Music API (shared by both platforms) ---

// An export sends a burst of requests (catalog searches + the playlist
// create), so Apple's limiter answering 429 is expected under normal use —
// back off and retry rather than failing the export.
const RATE_LIMIT_DELAYS_MS = [1000, 3000, 6000];
const MAX_RETRY_AFTER_MS = 10000;

function rateLimitDelay(res, attempt) {
  const retryAfter = Number(res.headers.get('Retry-After'));
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, MAX_RETRY_AFTER_MS);
  }
  return RATE_LIMIT_DELAYS_MS[attempt];
}

async function appleMusicFetch(path, options = {}) {
  const mut = await getMusicUserToken();
  if (!mut) {
    const err = new Error('Not connected to Apple Music.');
    err.code = 'not-authorized';
    throw err;
  }
  let devTokenRefreshed = false;
  let forceTokenRefresh = false;
  let rateLimitAttempt = 0;
  for (;;) {
    const token = await fetchDeveloperToken(forceTokenRefresh);
    forceTokenRefresh = false;
    const res = await fetch(`${API_ROOT}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Music-User-Token': mut,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    });
    // 401 once: the developer token expired between page load and the
    // request (server re-signs daily) — re-fetch and retry.
    if (res.status === 401 && !devTokenRefreshed) {
      devTokenRefreshed = true;
      forceTokenRefresh = true;
      continue;
    }
    if (res.status === 429 && rateLimitAttempt < RATE_LIMIT_DELAYS_MS.length) {
      const delay = rateLimitDelay(res, rateLimitAttempt);
      rateLimitAttempt += 1;
      await new Promise((resolve) => setTimeout(resolve, delay));
      continue;
    }
    return res;
  }
}

function apiError(res) {
  const err = new Error(
    res.status === 403
      ? 'Your Apple Music session expired.'
      : res.status === 429
        ? 'Apple Music is limiting requests right now — wait a minute and try again.'
        : `Apple Music request failed (${res.status}).`
  );
  err.status = res.status;
  if (res.status === 403) err.code = 'not-authorized';
  return err;
}

// The signed-in user's storefront (e.g. 'us', 'gb', 'de'). Searching the
// right storefront matters: catalog IDs and availability differ by region,
// and only songs in the user's storefront can land in their library.
let storefrontPromise = null;

async function getUserStorefront() {
  if (!storefrontPromise) {
    storefrontPromise = appleMusicFetch('/v1/me/storefront')
      .then(async (res) => {
        if (!res.ok) throw new Error(`storefront lookup ${res.status}`);
        const data = await res.json();
        const id = data?.data?.[0]?.id;
        if (!id) throw new Error('no storefront in response');
        return id;
      })
      .catch(() => {
        storefrontPromise = null; // transient — retry on the next export
        return 'us';
      });
  }
  return storefrontPromise;
}

// Successful matches cached for the session so re-exporting the same or an
// overlapping playlist doesn't re-spend rate budget. Misses are NOT cached:
// they're rare after the tiered matching below, and a transient failure must
// not pin a track as "not found" for the whole session.
const catalogSearchCache = new Map();

function catalogSearchKey(track) {
  return `${(track.artistName || '').toLowerCase()}\n${(track.name || '').toLowerCase()}`;
}

async function searchSongs(storefront, term) {
  const params = new URLSearchParams({ term, types: 'songs', limit: '25' });
  const res = await appleMusicFetch(`/v1/catalog/${storefront}/search?${params.toString()}`);
  if (!res.ok) {
    if (res.status === 403) throw apiError(res);
    return [];
  }
  const data = await res.json();
  return (data?.results?.songs?.data || []).map((s) => ({
    id: s.id,
    name: s.attributes?.name || '',
    artistName: s.attributes?.artistName || '',
  }));
}

// Exact recording lookup by ISRC — the recording code is shared across
// services, so if Deezer has it and Apple carries the recording this cannot
// mismatch. Only used for tracks text search failed on.
async function lookupByIsrc(storefront, track) {
  const isrc = await getTrackIsrc(track.id);
  if (!isrc) return null;
  const params = new URLSearchParams({ 'filter[isrc]': isrc });
  const res = await appleMusicFetch(`/v1/catalog/${storefront}/songs?${params.toString()}`);
  if (!res.ok) return null;
  const data = await res.json();
  const songs = data?.data || [];
  if (songs.length === 0) return null;
  // The same ISRC can appear on several albums (single, album, compilation);
  // prefer one credited to the wanted artist, else any is the right recording.
  const credited = songs.find((s) => artistsMatch(track.artistName, s.attributes?.artistName || ''));
  return (credited || songs[0]).id;
}

// Fallback matcher for tracks the iTunes link pipeline hasn't resolved:
// search the catalog directly (browser → Apple, so it doesn't touch the
// server's iTunes rate budget). Tiered: fuzzy-scored search over
// progressively cleaned title variants, then an exact ISRC lookup as the
// last resort. Returns a catalog song ID or null — never guesses.
export async function searchCatalogSong(track) {
  const key = catalogSearchKey(track);
  if (catalogSearchCache.has(key)) return catalogSearchCache.get(key);

  const storefront = await getUserStorefront();

  // Each variant strips more decoration ("(feat. X)", "- 2011 Remaster");
  // most tracks match on the first query, so later ones rarely run.
  for (const variant of titleVariants(track.name)) {
    const candidates = await searchSongs(storefront, `${variant} ${track.artistName}`);
    const match = pickBestSongMatch(candidates, track);
    if (match) {
      catalogSearchCache.set(key, match.id);
      return match.id;
    }
  }

  const isrcId = await lookupByIsrc(storefront, track);
  if (isrcId) catalogSearchCache.set(key, isrcId);
  return isrcId;
}

// Apple documents no hard cap on tracks-per-request; 100 is a conservative
// chunk that keeps request bodies small. Typical Stellar playlists fit in
// the create call alone.
const TRACKS_PER_REQUEST = 100;

export async function createLibraryPlaylist({ name, description, songIds }) {
  const toTrackData = (ids) => ids.map((id) => ({ id, type: 'songs' }));

  const res = await appleMusicFetch('/v1/me/library/playlists', {
    method: 'POST',
    body: JSON.stringify({
      attributes: { name, description },
      relationships: {
        tracks: { data: toTrackData(songIds.slice(0, TRACKS_PER_REQUEST)) },
      },
    }),
  });
  if (!res.ok) throw apiError(res);

  let playlistId = null;
  try {
    const data = await res.json();
    playlistId = data?.data?.[0]?.id || null;
  } catch {
    // 204-style empty body — playlist was still created.
  }

  for (let i = TRACKS_PER_REQUEST; i < songIds.length && playlistId; i += TRACKS_PER_REQUEST) {
    const addRes = await appleMusicFetch(`/v1/me/library/playlists/${playlistId}/tracks`, {
      method: 'POST',
      body: JSON.stringify({ data: toTrackData(songIds.slice(i, i + TRACKS_PER_REQUEST)) }),
    });
    if (!addRes.ok) throw apiError(addRes);
  }

  return { playlistId };
}
